import { Form, Formik, useField } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createFinancialStatement } from "@/features/bookkeeping/api";
import {
  STATEMENT_TYPE_OPTIONS,
  type FinancialStatement,
  type StatementType,
} from "@/features/bookkeeping/types";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { ApiError } from "@/lib/api";

/** Formik-bound Textarea for the optional summary, matching FormField's error style. */
function SummaryField() {
  const [field, meta] = useField<string>("summary");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor="summary">Summary</Label>
      <Textarea
        id="summary"
        rows={4}
        placeholder="Optional notes about this statement"
        aria-invalid={hasError}
        {...field}
      />
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}

const schema = Yup.object({
  period_label: Yup.string().trim().required("Period is required"),
  statement_type: Yup.string().required("Statement type is required"),
  summary: Yup.string(),
});

interface AddStatementValues {
  period_label: string;
  statement_type: StatementType;
  summary: string;
}

const initialValues: AddStatementValues = {
  period_label: "",
  statement_type: "PROFIT_LOSS",
  summary: "",
};

interface AddStatementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  onCreated: (statement: FinancialStatement) => void;
}

export function AddStatementDialog({
  open,
  onOpenChange,
  clientId,
  onCreated,
}: AddStatementDialogProps) {
  async function handleSubmit(values: AddStatementValues) {
    try {
      const statement = await createFinancialStatement(clientId, {
        period_label: values.period_label.trim(),
        statement_type: values.statement_type,
        summary: values.summary.trim() || undefined,
      });
      toast.success("Financial statement added");
      onCreated(statement);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add the statement.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add financial statement</DialogTitle>
          <DialogDescription>
            Record a prepared statement for a reporting period.
          </DialogDescription>
        </DialogHeader>
        <Formik<AddStatementValues>
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField
                name="period_label"
                label="Period"
                placeholder="e.g. Q1 2026 or FY2025"
                autoFocus
              />
              <SelectField
                name="statement_type"
                label="Statement type"
                options={STATEMENT_TYPE_OPTIONS}
              />
              <SummaryField />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Adding…" : "Add statement"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
